import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { SearchInput } from '@/components/SearchInput';
import { AvailabilityBadge } from '@/components/AvailabilityBadge';
import { QueryState } from '@/components/QueryState';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { useOrderCart } from '@/features/orders/useOrderCart';
import { SkuOrderRows } from './SkuOrderRows';
import { useProductList } from '@/data/hooks';
import { SortSelect } from '@/components/SortSelect';
import { byDate, byNumber, byText, sortItems, thenBy, type SortOption } from '@/lib/sort';
import type { CatalogProduct } from '@lombok/shared';

/** Active first, then inactive, then archived (only an admin sees the last two). */
const statusRank = (p: CatalogProduct) => (p.archived ? 2 : p.active ? 0 : 1);

const PRODUCT_SORT: SortOption<CatalogProduct>[] = [
  { value: 'recent', label: 'Más recientes', compare: byDate((p) => p.createdAt, 'desc') },
  { value: 'az', label: 'Nombre A–Z', compare: byText((p) => p.name) },
  { value: 'za', label: 'Nombre Z–A', compare: byText((p) => p.name, 'desc') },
  { value: 'status', label: 'Estado', compare: thenBy<CatalogProduct>(byNumber(statusRank), byText((p) => p.name)) },
];
/** Everyone else only sees active products, so ordering by status would mean nothing. */
const PRODUCT_SORT_PUBLIC = PRODUCT_SORT.filter((o) => o.value !== 'status');

/**
 * `basePath` enables admin create/detail links + a "Nuevo producto" action.
 * When omitted (customer/distributor catalog) the list stays read-only with no
 * row links. Admin sees exact stock elsewhere (Inventario); this catalog view is
 * the same customer-safe availability surface in every role.
 */
interface ProductsListPageProps {
  basePath?: string;
  /**
   * Customer catalog: each product expands to its presentations with price and "Agregar", and the
   * header shows the order in progress. Off for the admin list and the distributor catalog.
   */
  orderable?: boolean;
}

/**
 * Productos (catalog) list. Fetches via the typed API client + TanStack Query
 * and renders a responsive table (desktop) / card list (mobile). Each row carries the
 * server-derived availability label (best across the product's SKUs) — only the label,
 * never exact stock, which is never exposed to customers anyway.
 */
export function ProductsListPage({ basePath, orderable = false }: ProductsListPageProps = {}) {
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [sort, setSort] = useState('recent');
  const [openId, setOpenId] = useState<string | null>(null);
  const { profile } = useSessionProfile();
  const cart = useOrderCart(orderable ? profile?.customerId : undefined);
  // Admin (a basePath in the admin group AND the admin role; finance only reads) also sees inactive and archived products; the rest see the active catalog.
  const isAdmin = useIsAdmin();
  const adminView = Boolean(basePath) && isAdmin;
  const query = useProductList(adminView);

  const items = query.data?.items ?? [];
  const sortOptions = adminView ? PRODUCT_SORT : PRODUCT_SORT_PUBLIC;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortItems(
      items.filter(
        (p) =>
          (showArchived || !p.archived) &&
          (!q || p.name.toLowerCase().includes(q) || (p.description?.toLowerCase().includes(q) ?? false)),
      ),
      sortOptions,
      sort,
    );
  }, [items, search, showArchived, sortOptions, sort]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Productos"
        description={orderable ? 'Catálogo, precios y disponibilidad.' : 'Catálogo y disponibilidad.'}
        actions={
          adminView ? (
            <Link to={`${basePath}/nuevo`}>
              <Button size="sm">Nuevo producto</Button>
            </Link>
          ) : orderable && cart.count > 0 ? (
            <Link to="/app/customer/crear-pedido">
              <Button size="sm">Ver pedido ({cart.count})</Button>
            </Link>
          ) : undefined
        }
      />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center">
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Buscar producto…" />
          </div>
          <SortSelect options={sortOptions} value={sort} onChange={setSort} />
          {adminView ? (
            <label className="inline-flex select-none items-center gap-2 text-sm text-text-primary">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-5 w-5 rounded border-border-strong text-brand-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              Mostrar archivados
            </label>
          ) : null}
        </div>

        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          isEmpty={filtered.length === 0}
          loadingLabel="Cargando productos…"
          loadingVariant="table"
          skeletonCols={4}
          emptyTitle={search ? 'Sin coincidencias' : 'No hay productos'}
          emptyDescription={
            search
              ? 'Ajusta la búsqueda para ver más resultados.'
              : 'Aún no se han cargado productos en el catálogo.'
          }
        >
          {/* Desktop table */}
          <div className="hidden sm:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                  <th className="px-4 py-3 font-medium">Producto</th>
                  <th className="px-4 py-3 font-medium">Descripción</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Disponibilidad</th>
                  {orderable ? <th className="px-4 py-3 font-medium" /> : null}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <Fragment key={p.productId}>
                  <tr className="border-b border-border last:border-0 hover:bg-surface-muted">
                    <td className="px-4 py-3 font-medium text-text-primary">
                      {basePath ? (
                        <Link
                          to={`${basePath}/${p.productId}`}
                          className="text-brand-primary hover:underline"
                        >
                          {p.name}
                        </Link>
                      ) : (
                        p.name
                      )}
                    </td>
                    <td className="max-w-xs truncate px-4 py-3 text-text-muted">
                      {p.description?.trim() || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={p.archived ? 'neutral' : p.active ? 'success' : 'warning'}>
                        {p.archived ? 'Archivado' : p.active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <AvailabilityBadge status={p.availability} />
                    </td>
                    {orderable ? (
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          aria-expanded={openId === p.productId}
                          onClick={() => setOpenId(openId === p.productId ? null : p.productId)}
                        >
                          {openId === p.productId ? 'Ocultar' : 'Ver presentaciones'}
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                  {orderable && openId === p.productId ? (
                    <tr className="border-b border-border bg-surface-muted/40 last:border-0">
                      <td colSpan={5} className="p-0">
                        <SkuOrderRows productId={p.productId} cart={cart} />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards (no tiny table actions on phones). */}
          <ul className="divide-y divide-border sm:hidden">
            {filtered.map((p) => (
              <li key={p.productId} className="flex flex-col gap-2 p-4">
                <div className="flex items-start justify-between gap-3">
                  {basePath ? (
                    <Link
                      to={`${basePath}/${p.productId}`}
                      className="font-medium text-brand-primary active:underline"
                    >
                      {p.name}
                    </Link>
                  ) : (
                    <span className="font-medium text-text-primary">{p.name}</span>
                  )}
                  <AvailabilityBadge status={p.availability} />
                </div>
                {p.archived || !p.active ? (
                  <Badge tone="neutral" className="w-fit">
                    {p.archived ? 'Archivado' : 'Inactivo'}
                  </Badge>
                ) : null}
                {p.description ? (
                  <p className="text-sm text-text-muted">{p.description}</p>
                ) : null}
                {orderable ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-fit"
                      aria-expanded={openId === p.productId}
                      onClick={() => setOpenId(openId === p.productId ? null : p.productId)}
                    >
                      {openId === p.productId ? 'Ocultar' : 'Ver presentaciones'}
                    </Button>
                    {openId === p.productId ? (
                      <div className="-mx-4 rounded-md bg-surface-muted/40">
                        <SkuOrderRows productId={p.productId} cart={cart} />
                      </div>
                    ) : null}
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}
